import { useEffect, useState } from 'react';

export type LayoutMode = 'phone' | 'tablet' | 'desktop';

/** Matches mobile-web-editor-architecture breakpoints. */
export const LAYOUT_BREAKPOINTS = {
  phoneMax: 767,
  tabletMax: 1099,
} as const;

function modeFromWidth(width: number): LayoutMode {
  if (width <= LAYOUT_BREAKPOINTS.phoneMax) return 'phone';
  if (width <= LAYOUT_BREAKPOINTS.tabletMax) return 'tablet';
  return 'desktop';
}

export function readLayoutMode(): LayoutMode {
  if (typeof window === 'undefined') return 'desktop';
  return modeFromWidth(window.innerWidth);
}

/**
 * Responsive editor layout mode (phone / tablet / desktop).
 * Uses matchMedia so it updates on rotate and window resize.
 */
export function useLayoutMode(): LayoutMode {
  const [mode, setMode] = useState<LayoutMode>(() => readLayoutMode());

  useEffect(() => {
    const phone = window.matchMedia(
      `(max-width: ${LAYOUT_BREAKPOINTS.phoneMax}px)`
    );
    const tablet = window.matchMedia(
      `(min-width: ${LAYOUT_BREAKPOINTS.phoneMax + 1}px) and (max-width: ${LAYOUT_BREAKPOINTS.tabletMax}px)`
    );

    const sync = () => setMode(readLayoutMode());
    sync();

    phone.addEventListener('change', sync);
    tablet.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    return () => {
      phone.removeEventListener('change', sync);
      tablet.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  return mode;
}

export function isCompactLayout(mode: LayoutMode): boolean {
  return mode === 'phone' || mode === 'tablet';
}
